/**
 * Shared Polling Utility
 * 
 * Provides smart polling with:
 * - Visibility-aware pausing (stops when tab is hidden)
 * - Completion detection (stops when condition is met)
 * - Adaptive intervals (slows down over time)
 * - Request deduplication (prevents overlapping requests)
 * 
 * Usage:
 *   const poller = createPoller({
 *     fn: async () => {
 *       const data = await fetchData();
 *       return { data, isComplete: data.status === 'complete' };
 *     },
 *     interval: 5000,
 *     onComplete: (data) => { console.log('Done!', data); },
 *     onError: (err) => { console.error(err); },
 *   });
 *   
 *   poller.start();
 *   // ... later
 *   poller.stop();
 */

export interface PollerOptions<T> {
  /** The async function to poll. Return { data, isComplete } to signal completion. */
  fn: () => Promise<{ data: T; isComplete: boolean }>;
  /** Initial polling interval in milliseconds (default: 5000) */
  interval?: number;
  /** Maximum polling interval when using adaptive mode (default: 30000) */
  maxInterval?: number;
  /** Whether to increase interval over time (default: true) */
  adaptive?: boolean;
  /** Called when polling completes (isComplete returned true) */
  onComplete?: (data: T) => void;
  /** Called on each successful poll (even if not complete) */
  onData?: (data: T) => void;
  /** Called on error */
  onError?: (error: Error) => void;
  /** Maximum number of poll attempts (default: 120) */
  maxAttempts?: number;
  /** Called when max attempts reached */
  onMaxAttempts?: () => void;
}

export interface Poller {
  start: () => void;
  stop: () => void;
  isPolling: () => boolean;
  getAttempts: () => number;
}

export function createPoller<T>(options: PollerOptions<T>): Poller {
  const {
    fn,
    interval = 5000,
    maxInterval = 30000,
    adaptive = true,
    onComplete,
    onData,
    onError,
    maxAttempts = 120,
    onMaxAttempts,
  } = options;

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let currentInterval = interval;
  let attempts = 0;
  let isRunning = false;
  let isPaused = false;
  let inFlight = false;

  // Handle visibility changes
  function handleVisibilityChange() {
    if (typeof document === 'undefined') return;
    
    if (document.hidden) {
      isPaused = true;
      // Clear pending timeout when hidden
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    } else if (isPaused && isRunning) {
      isPaused = false;
      // Resume polling immediately when visible again
      poll();
    }
  }

  function scheduleNext() {
    if (!isRunning || isPaused) return;
    
    timeoutId = setTimeout(poll, currentInterval);
  }

  async function poll() {
    if (!isRunning || isPaused || inFlight) return;

    attempts++;
    
    if (attempts > maxAttempts) {
      stop();
      onMaxAttempts?.();
      return;
    }

    inFlight = true;
    
    try {
      const result = await fn();
      
      onData?.(result.data);
      
      if (result.isComplete) {
        stop();
        onComplete?.(result.data);
        return;
      }
      
      // Adaptive interval: increase by 20% each attempt, up to max
      if (adaptive && currentInterval < maxInterval) {
        currentInterval = Math.min(currentInterval * 1.2, maxInterval);
      }
      
      scheduleNext();
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
      // Continue polling on error (exponential backoff)
      if (adaptive) {
        currentInterval = Math.min(currentInterval * 1.5, maxInterval);
      }
      scheduleNext();
    } finally {
      inFlight = false;
    }
  }

  function start() {
    if (isRunning) return;
    
    isRunning = true;
    isPaused = false;
    attempts = 0;
    currentInterval = interval;
    
    // Add visibility listener
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    
    // Start immediately
    poll();
  }

  function stop() {
    isRunning = false;
    isPaused = false;
    
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    
    // Remove visibility listener
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  }

  return {
    start,
    stop,
    isPolling: () => isRunning,
    getAttempts: () => attempts,
  };
}

/**
 * Simple debounce utility
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Simple throttle utility
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      fn(...args);
    }
  };
}
